import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Modal } from "components/designV2/modal/modal";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import { AdjustSalaryModalForm } from "./adjustSalaryModal.form.component";
import { useAdjustSalary } from "./adjustSalary.hook";
import { AdjustSalaryModalList } from "./adjustSalaryModal.list.component";
import type { SalaryAdjustment } from "./adjustSalary.logic";
import { useAdjustSalaryModalDraft } from "./adjustSalaryModal.hook";
import { salaryAdjustmentPresentation } from "./adjustSalaryModal.presentation";
import { ErrorMessage, WorkerName } from "./adjustSalaryModal.styled";

interface Props {
  readonly companyId: number;
  readonly initialAdjustments: readonly SalaryAdjustment[];
  readonly open: boolean;
  readonly salaryNoticeId: number;
  readonly workerId: number;
  readonly workerName: string;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}

export const AdjustSalaryModal = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const { save, status } = useAdjustSalary(props.companyId);
  const isSubmitting = status.type === "submitting";
  const draft = useAdjustSalaryModalDraft({
    initialAdjustments: props.initialAdjustments,
    isSubmitting,
  });
  const isSuccess = status.type === "success";
  const errorMessage = status.type === "error" ? status.message : null;
  const adjustmentOptions = draft.availableKeys.map((key) => ({
    label: t(salaryAdjustmentPresentation[key].labelTranslationKey),
    value: String(key),
  }));

  const handleSave = (): void => {
    if (
      !draft.hasChanges ||
      draft.editState.type === "editing" ||
      isSubmitting
    ) {
      return;
    }
    save({
      adjustments: draft.adjustments,
      initialAdjustments: props.initialAdjustments,
      salaryNoticeId: props.salaryNoticeId,
      workerId: props.workerId,
    });
  };

  const handleClose = (): void => {
    if (!isSubmitting) props.onClose();
  };

  if (isSuccess) {
    return (
      <Modal
        isOpen={props.open}
        label={t("Salary.Changed")}
        size="compact"
        footer={
          <PrimaryButton
            label={t("OrganizationalStructure.Ok")}
            onClick={props.onSaved}
            size="small"
          />
        }
        onClose={props.onSaved}
      >
        <Text typography="b2" color={ColorV2.Blue1}>
          {t("Salary.SalaryAdjusted")}
        </Text>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={props.open}
      label={t("Salary.AdjustSalary")}
      size="mediumSlim"
      footer={
        <>
          <SecondaryButton
            disabled={isSubmitting}
            label={t("Salary.Cancel")}
            size="small"
            onClick={handleClose}
          />
          <PrimaryButton
            disabled={
              !draft.hasChanges ||
              draft.editState.type === "editing" ||
              isSubmitting
            }
            isLoading={isSubmitting}
            label={t("Salary.Save")}
            size="small"
            onClick={handleSave}
          />
        </>
      }
      onClose={handleClose}
    >
      <WorkerName>{props.workerName}</WorkerName>
      <AdjustSalaryModalForm
        addFormState={draft.addFormState}
        isAmountValid={draft.isAmountValid}
        isSubmitting={isSubmitting}
        options={adjustmentOptions}
        onAdd={draft.handleAdd}
        onAmountChange={draft.handleAmountChange}
        onSelectedTypeChange={draft.handleSelectedTypeChange}
      />
      <AdjustSalaryModalList
        adjustments={draft.adjustments}
        editState={draft.editState}
        isEditingAmountValid={draft.isEditingAmountValid}
        isSubmitting={isSubmitting}
        onCancelEdit={draft.handleCancelEdit}
        onConfirmEdit={draft.handleConfirmEdit}
        onEdit={draft.handleEdit}
        onEditingAmountChange={draft.handleEditingAmountChange}
        onRemove={draft.handleRemove}
      />

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Modal>
  );
};
